import { Link } from "react-router-dom";
import { Newspaper, ExternalLink, MapPin, Quote, ArrowUpRight, Calendar } from "lucide-react";
import SafeImage from "./SafeImage";

/**
 * NewspaperFeature — three visual variants for surfacing a print press
 * mention across the site.  Data comes from `/data/press.js`.
 *
 *   • variant="full"     — hero card (Press page, Dr. Manisha page).
 *                          Full-width, big close-up scan, pull quote,
 *                          typeset article body, district pill list.
 *   • variant="compact"  — condensed strip (squint landing page).
 *                          Left masthead label + pull quote + link.
 *   • variant="band"     — homepage "As featured in" bar.  Masthead only.
 */
const NewspaperFeature = ({ mention, variant = "full" }) => {
  if (!mention) return null;

  if (variant === "band") {
    return (
      <Link
        to={`/press#${mention.id}`}
        className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-background hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]/50 transition-colors"
        data-testid={`press-band-${mention.id}`}
      >
        <Newspaper size={13} className="text-[hsl(var(--accent))]" />
        <span className="font-serif text-base leading-none text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))]">
          {mention.masthead}
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-l border-border pl-2 ml-0.5">
          {mention.dateLabel}
        </span>
        <ArrowUpRight size={13} className="text-[hsl(var(--accent))] opacity-70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <section
        className="py-10 border-y border-border bg-[hsl(var(--secondary))]/40"
        data-testid={`press-compact-${mention.id}`}
      >
        <div className="container-page">
          <div className="flex flex-col md:flex-row md:items-start md:gap-8">
            <div className="md:w-1/3">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-2">
                In the news
              </div>
              <div className="font-serif text-2xl leading-tight text-[hsl(var(--foreground))]">
                {mention.masthead}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                <Calendar size={11} /> {mention.dateLabel} · {mention.edition}
              </div>
            </div>
            <div className="mt-6 md:mt-0 md:flex-1">
              <Quote size={22} className="text-[hsl(var(--accent))] opacity-60 mb-2" strokeWidth={1.5} />
              <blockquote className="font-serif text-lg sm:text-xl leading-snug text-[hsl(var(--foreground))]">
                {mention.pullQuoteHi}
              </blockquote>
              <p className="text-sm text-muted-foreground italic mt-3">
                — {mention.pullQuoteEn}
              </p>
              <Link
                to={`/press#${mention.id}`}
                className="mt-5 inline-flex items-center gap-1.5 text-sm text-[hsl(var(--primary))] hover:text-[hsl(var(--foreground))] underline decoration-dotted underline-offset-4"
                data-testid={`press-compact-cta-${mention.id}`}
              >
                Read the full feature <ArrowUpRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Full variant.
  return (
    <article
      id={mention.id}
      className="scroll-mt-24 border border-border rounded-2xl overflow-hidden bg-background"
      data-testid={`press-full-${mention.id}`}
    >
      <div className="grid md:grid-cols-2 gap-0">
        {/* Article scan */}
        <div className="relative border-b md:border-b-0 md:border-r border-border bg-[hsl(var(--secondary))]/40">
          <SafeImage
            src={mention.imageClose}
            alt={`${mention.titleEn} — ${mention.masthead}, ${mention.dateLabel}`}
            className="w-full h-full object-cover object-top"
            data-testid={`press-image-${mention.id}`}
          />
          <a
            href={mention.imageFull}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/90 backdrop-blur border border-border text-[11px] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
            data-testid={`press-fullpage-${mention.id}`}
          >
            View full newspaper page <ExternalLink size={11} />
          </a>
        </div>

        {/* Article typeset */}
        <div className="p-6 sm:p-8 md:p-10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">
            <Newspaper size={12} />
            <span>Featured coverage</span>
          </div>
          <div className="font-serif text-2xl mt-2 leading-tight text-[hsl(var(--foreground))]">
            {mention.masthead}
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {mention.dateLabel}</span>
            <span>·</span>
            <span>{mention.edition}</span>
          </div>

          <h3
            lang="hi"
            className="font-serif text-xl sm:text-2xl mt-6 leading-snug text-[hsl(var(--foreground))]"
          >
            {mention.titleHi}
          </h3>
          <p className="text-xs italic text-muted-foreground mt-1">{mention.titleEn}</p>

          <div className="h-[2px] w-10 bg-[hsl(var(--accent))] mt-5 mb-5" />

          <blockquote
            lang="hi"
            className="border-l-2 border-[hsl(var(--accent))] pl-4 font-serif text-base sm:text-lg leading-snug text-[hsl(var(--foreground))]/90"
          >
            {mention.pullQuoteHi}
          </blockquote>
          <p className="text-xs italic text-muted-foreground mt-2">{mention.pullQuoteEn}</p>

          {mention.districts?.length > 0 && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                <MapPin size={11} /> Districts of print circulation
              </div>
              <div className="flex flex-wrap gap-1.5">
                {mention.districts.map((d) => (
                  <span
                    key={d}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-border bg-background text-[hsl(var(--foreground))]/80"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {mention.epaperUrl && (
              <a
                href={mention.epaperUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-border hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]/60 transition-colors"
                data-testid={`press-epaper-${mention.id}`}
              >
                Open on Jagran e-paper <ExternalLink size={12} />
              </a>
            )}
            {mention.subject?.pageHref && (
              <Link
                to={mention.subject.pageHref}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity"
                data-testid={`press-subject-${mention.id}`}
              >
                About {mention.subject.name} <ArrowUpRight size={12} />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Typeset body — search-indexable Hindi text */}
      {mention.bodyParagraphsHi?.length > 0 && (
        <div className="p-6 sm:p-8 md:p-10 border-t border-border">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">
            Article text (as printed)
          </div>
          <div lang="hi" className="max-w-3xl space-y-4 prose-elegant text-[15px] leading-relaxed">
            {mention.bodyParagraphsHi.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export default NewspaperFeature;
